import { _js } from '@ifixit/localize';
import { memo, Fragment } from 'react';
import DiagramTooltip from '../Generic/diagramTooltip';
import FloatingPartsList from '../MiscParts/floating_parts_list';
import { MinimalDisclosure } from 'Guide/Schematics/Diagram/Logic/minimalDisclosure';
import { useSearchContext } from 'Guide/Schematics/Diagram/Providers/searchContext';
import { useDataContext } from 'Guide/Schematics/Diagram/Providers/dataContext';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';
import { faList, faXmark } from '@fortawesome/pro-regular-svg-icons';
import {
   Flex,
   FlexProps,
   TextProps,
   Text,
   Box,
   Button,
   ButtonProps,
   Show,
   IconButtonProps,
   IconButton,
   BoxProps,
} from '@chakra-ui/react';
import { FaIcon, FaIconProps } from '@ifixit/icons';

export function HeaderWrapper(props: FlexProps) {
   return (
      <Flex
         background="gray.50"
         justifyContent="space-between"
         alignItems="center"
         padding="13px 16px"
         {...props}
      >
         {props.children}
      </Flex>
   );
}

function HeaderContainer(props: FlexProps) {
   return (
      <HeaderWrapper
         position="fixed"
         top={0}
         left={0}
         right={0}
         margin="16px 32px 0px"
         boxShadow="2xl"
         borderRadius="8px"
         zIndex={9999}
         {...props}
      >
         {props.children}
      </HeaderWrapper>
   );
}

function PartsListIcon(props: Omit<FaIconProps, 'icon'>) {
   return (
      <FaIcon icon={faList} fontSize="14px" color="gray.800" verticalAlign="middle" {...props} />
   );
}

function ExitIcon(props: Omit<FaIconProps, 'icon'>) {
   return (
      <FaIcon icon={faXmark} fontSize="14px" color="gray.800" verticalAlign="middle" {...props} />
   );
}

function HeaderButton({ children, ...props }: ButtonProps) {
   return (
      <Button
         variant="outline"
         colorScheme="gray"
         background="gray.50"
         borderWidth="1px"
         borderColor="gray.300"
         borderRadius="md"
         fontWeight="bold"
         fontSize={{ base: '0px', md: '14px' }}
         textAlign="center"
         padding={{ base: '10px 10px', md: '10px 16px' }}
         {...props}
      >
         {children}
      </Button>
   );
}

function PartsListButton({ children, rightIcon, ...props }: ButtonProps & IconButtonProps) {
   return (
      <>
         <Show below="md">
            <HeaderButton as={IconButton} icon={rightIcon} {...props} />
         </Show>
         <Show above="md">
            <HeaderButton rightIcon={rightIcon} {...props}>
               {children}
            </HeaderButton>
         </Show>
      </>
   );
}

export function ExitButton({ children, ...props }: ButtonProps & IconButtonProps) {
   return (
      <>
         <Show below="md">
            <HeaderButton as={IconButton} icon={<ExitIcon />} {...props} />
         </Show>
         <Show above="md">
            <HeaderButton {...props}>{children}</HeaderButton>
         </Show>
      </>
   );
}

function Title(props: TextProps) {
   return (
      <Text
         fontWeight="bold"
         fontSize={{ base: '14px', md: '18px' }}
         textAlign="center"
         color="black"
         padding="8px"
         noOfLines={1}
         whiteSpace="nowrap"
         _focus={{ outline: 'none' }}
         flexGrow={1}
         {...props}
      >
         {props.children}
      </Text>
   );
}

function SubTitle(props: TextProps) {
   return (
      <Text fontSize="12px" textAlign="center" color="gray.600" {...props}>
         {props.children}
      </Text>
   );
}

export const TitleWithTooltip = memo(function TitleWithTooltipInternal({
   title,
   workOrder,
   ...props
}: {
   title: string;
   workOrder?: string;
} & BoxProps) {
   const content = (
      <Fragment>
         {title}
         {workOrder && <SubTitle>{_js('Work Order: %1', workOrder)}</SubTitle>}
      </Fragment>
   );

   return (
      <DiagramTooltip content={content}>
         <Box {...props}>
            <Title padding="0">{title}</Title>
            {workOrder && <SubTitle>{_js('Work Order: %1', workOrder)}</SubTitle>}
         </Box>
      </DiagramTooltip>
   );
});

function Header({ onClose, title }: { title: string } & Pick<MinimalDisclosure, 'onClose'>) {
   const searchContext = useSearchContext<iFixitHotSpotItem>();
   const dataContext = useDataContext<iFixitHotSpotItem>();
   const partsListCount = dataContext.data.length;
   const { onToggle } = searchContext.disclosure;

   return (
      <HeaderContainer>
         <ExitButton onClick={onClose} aria-label="Close Diagram Viewer">
            {_js('Exit Viewer')}
         </ExitButton>
         <TitleWithTooltip title={title} />
         <PartsListButton
            onClick={onToggle}
            rightIcon={<PartsListIcon />}
            aria-label="Show Parts List"
         >
            {_js('Show Parts List (%1)', partsListCount.toString())}
         </PartsListButton>
         <FloatingPartsList />
      </HeaderContainer>
   );
}
export default memo(Header);
